import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import TaskOutlinedIcon from "@mui/icons-material/TaskOutlined";

const statusLabel = (status) =>
  String(status || "unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (ch) => ch.toUpperCase());

const sortByLatest = (a, b) => {
  const aSec = Number(a?.updatedAt?.seconds || a?.updatedAt?._seconds || a?.createdAt?.seconds || 0);
  const bSec = Number(b?.updatedAt?.seconds || b?.updatedAt?._seconds || b?.createdAt?.seconds || 0);
  return bSec - aSec;
};

const Timesheets = ({
  loading,
  timesheets = [],
  busyById = {},
  onResolve,
  formatCurrency,
  formatDate,
  resolveUserLabel,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const sorted = useMemo(() => [...timesheets].sort(sortByLatest), [timesheets]);
  const rows = useMemo(
    () => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sorted, page, rowsPerPage]
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: "8px", mb: 2, display: "flex", flexDirection: "column" }}>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
        <Avatar variant="rounded" sx={{ width: 28, height: 28, bgcolor: "grey.200", color: "grey.700" }}>
          <TaskOutlinedIcon fontSize="small" />
        </Avatar>
        <Typography sx={{ fontWeight: 600 }}>Timesheets</Typography>
      </Stack>

      {loading ? (
        <Stack spacing={1} sx={{ flexGrow: 1 }}>
          {Array.from({ length: 5 }).map((_, idx) => (
            <Skeleton key={idx} variant="rounded" height={40} />
          ))}
        </Stack>
      ) : sorted.length === 0 ? (
        <Box sx={{ flexGrow: 1, minHeight: 140, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="body2" color="text.secondary">No open timesheets.</Typography>
        </Box>
      ) : (
        <TableContainer sx={{ flexGrow: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Month</TableCell>
                <TableCell>Contractor</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Hours</TableCell>
                <TableCell align="right">Cost</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const busy = !!busyById[row.key];
                return (
                  <TableRow key={row.key} hover>
                    <TableCell>{row.month || row.id || "—"}</TableCell>
                    <TableCell>{resolveUserLabel?.(row.userId, row.userId || "—")}</TableCell>
                    <TableCell>
                      <Chip size="small" label={statusLabel(row.status)} sx={{ borderRadius: 1 }} />
                    </TableCell>
                    <TableCell align="right">{Number(row.totalHours || 0).toFixed(2)}</TableCell>
                    <TableCell align="right">{formatCurrency(Number(row.totalCost || 0), row.currency || "usd")}</TableCell>
                    <TableCell>{formatDate(row.updatedAt || row.createdAt)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={busy}
                          onClick={() => onResolve?.(row, "approved")}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          color="error"
                          disabled={busy}
                          onClick={() => onResolve?.(row, "rejected")}
                        >
                          Reject
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  count={sorted.length}
                  page={page}
                  onPageChange={(_event, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 20]}
                  labelRowsPerPage="Show"
                  colSpan={7}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default Timesheets;
